import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { v4 as uuid } from 'uuid';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../../models/NameUtils';
import { useAdminAccountQuery, useUserQuery } from '../../../../../queries';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../components-v2/Select';
import { IConnectionTypeComponentProps } from '../../../../containers/templates/EditCampaign/context';
import { Checkbox } from '../../../Checkbox';
import { ISelectOption } from '../../../DeprecatedSelect';
import {
	SelectAnEmployeeExternalEmailBody,
	SelectAnEmployeeInternalEmailBody,
	SelectAnEmployeeModal,
} from '../../../SelectAnEmployee';
import { styleSheet } from './../styles';

type StepProp =
	| Api.IAutomationStep
	| Api.IEmailAutomationStep
	| Api.ITextingAutomationStep
	| Api.IHtmlNewsletterAutomationStep;

interface Props extends IConnectionTypeComponentProps {
	canEdit?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	step: StepProp;
	sendFromLabel?: string;
	showInternalSendFromOption?: boolean;
	isBusy?: boolean;
	onSave?: (option: Api.ISendFromOptions, sendToSender: boolean) => void;
}

export function StepSendFromOption(props: Props) {
	const userSession = useUserSession();
	const canEdit = props.canEdit !== false;
	const automationStep = props.step;
	const hasSendOptionSelected = () => {
		return !!automationStep?.sendFromOptions && automationStep.sendFromOptions.mode !== Api.SendEmailFrom.CurrentUser;
	};
	const [selectAnEmployeeModalIsOpen, setSelectAnEmployeeModalIsOpen] = React.useState(false);
	const [employeeModalForSendToSender, setEmployeeModalForSendToSender] = React.useState(false);
	const [sendFrom, setSendFrom] = React.useState(automationStep?.sendFromOptions);
	const [sendToSender, setSendToSender] = React.useState(
		!!(automationStep as Api.IEmailAutomationStep)?.options?.sendToSender
	);
	const [showingSendFrom, setShowingSendFrom] = React.useState(() => {
		return hasSendOptionSelected() && !sendToSender;
	});

	const [selectedSendFromUser, setSelectedSendFromUser] = React.useState<Api.IUser | null>();
	const onSaveSelectAnEmployee = (user: Api.IUser) => {
		if (!canEdit) {
			return;
		}
		setSelectAnEmployeeModalIsOpen(false);
		setSelectedSendFromUser(user);
		const updatedOption = { mode: Api.SendEmailFrom.SelectedUser, selectedUser: user.id };
		setSendFrom(updatedOption);
		props.onSave(updatedOption, employeeModalForSendToSender);
	};
	const connectionOptions = props.connectionTypes?.length
		? props.connectionTypes.map(connectionType => {
				return {
					id: `connectionType-${connectionType.value}`,
					dataContext: {
						mode: Api.SendEmailFrom.ConnectionType,
						connectionType: connectionType.value,
					} as Api.ISendFromOptions,
					text: connectionType.label,
				};
			})
		: [];
	const sendFromOptions = [
		{
			id: Api.SendEmailFrom.ContactOwner,
			dataContext: { mode: Api.SendEmailFrom.ContactOwner },
			text: 'Contact Owner',
		},
		...connectionOptions,
		{
			id: Api.SendEmailFrom.SelectedUser,
			dataContext: { mode: Api.SendEmailFrom.SelectedUser },
			text: 'Selected User',
		},
	];
	const onSendFromSelectionChanged = (option: ISelectOption<Api.ISendFromOptions>, wasSelected: boolean) => {
		if (!canEdit) {
			return;
		}
		if (wasSelected) {
			if (option.dataContext.mode === Api.SendEmailFrom.SelectedUser) {
				setEmployeeModalForSendToSender(false);
				setSelectAnEmployeeModalIsOpen(true);
				return;
			}
			setSelectedSendFromUser(null);
			setSendFrom(option.dataContext);
			props.onSave(option.dataContext, false);
		}
	};
	const onSendToSenderSelectionChanged = (option: ISelectOption<Api.ISendFromOptions>, wasSelected: boolean) => {
		if (!canEdit) {
			return;
		}
		if (wasSelected) {
			if (option.dataContext.mode === Api.SendEmailFrom.SelectedUser) {
				setEmployeeModalForSendToSender(true);
				setSelectAnEmployeeModalIsOpen(true);
				return;
			}
			setSelectedSendFromUser(null);
			setSendFrom(option.dataContext);
			props.onSave(option.dataContext, true);
		}
	};
	const onCloseSelectAnEmployeeModal = () => setSelectAnEmployeeModalIsOpen(false);
	const selectedUserQuery = useUserQuery({
		enabled: Boolean(automationStep?.sendFromOptions?.selectedUser),
		id: automationStep?.sendFromOptions?.selectedUser ?? '',
		impersonationContext: props.impersonationContext,
	});

	// When impersonating an account (admin context), fetch the impersonated account's
	// features instead of relying on userSession.account which is the admin's own account.
	const impersonatedAccountId = props.impersonationContext?.account?.id;
	const impersonatedAccountQuery = useAdminAccountQuery({
		accountId: impersonatedAccountId ?? '',
		enabled: Boolean(impersonatedAccountId),
	});
	const showInternalSendOption = impersonatedAccountId
		? (impersonatedAccountQuery.data?.features?.automation?.showInternalSendOption ?? false)
		: (userSession.account.features?.automation?.showInternalSendOption ?? false);

	// Sync state when automationStep data changes (e.g., after refresh)
	const sendToSenderOption = (automationStep as Api.IEmailAutomationStep)?.options?.sendToSender;
	React.useEffect(() => {
		if (!automationStep) {
			return;
		}

		const stepSendFromOptions = automationStep.sendFromOptions;
		const stepSendToSender = !!sendToSenderOption;

		setSendFrom(prev => (JSON.stringify(prev) !== JSON.stringify(stepSendFromOptions) ? stepSendFromOptions : prev));
		setSendToSender(prev => (prev !== stepSendToSender ? stepSendToSender : prev));

		const hasOption = !!stepSendFromOptions && stepSendFromOptions.mode !== Api.SendEmailFrom.CurrentUser;
		const newShowingSendFrom = hasOption && !stepSendToSender;
		setShowingSendFrom(prev => (prev !== newShowingSendFrom ? newShowingSendFrom : prev));
	}, [automationStep, sendToSenderOption]);
	const onShowSendFromCheckboxChanged = () => {
		if (!canEdit) {
			return;
		}
		if (showingSendFrom) {
			setSendFrom(null);
			setShowingSendFrom(false);
			setSelectedSendFromUser(null);
			props.onSave(null, sendToSender);
		} else {
			const newSendFrom = sendFromOptions[0].dataContext;
			const newSendToSender = false;
			setSendFrom(newSendFrom);
			setShowingSendFrom(true);
			setSendToSender(newSendToSender);
			setSelectedSendFromUser(null);
			props.onSave(newSendFrom, newSendToSender);
		}
	};
	const getSendFromOptionLabel = (option: Api.ISendFromOptions) => {
		if (!option) {
			return null;
		}
		const allOptions = sendFromOptions;
		if (!allOptions) {
			return null;
		}

		if (option.mode === Api.SendEmailFrom.SelectedUser) {
			if (selectedSendFromUser) {
				return getDisplayName(selectedSendFromUser);
			}

			return selectedUserQuery.data ? getDisplayName(selectedUserQuery.data) : 'Selected User';
		}
		return allOptions.find(
			i => i.dataContext.mode === option.mode && i.dataContext.connectionType === option.connectionType
		)?.text;
	};
	const onSendToSenderCheckboxChanged = () => {
		if (!canEdit) {
			return;
		}
		if (sendToSender) {
			const newSendToSender = false;
			setSendFrom(null);
			setSendToSender(newSendToSender);
			setSelectedSendFromUser(null);
			props.onSave(null, newSendToSender);
		} else {
			const newSendFrom = sendFromOptions[0].dataContext;
			const newSendToSender = true;
			setSendFrom(newSendFrom);
			setSendToSender(newSendToSender);
			setShowingSendFrom(false);
			setSelectedSendFromUser(null);
			props.onSave(newSendFrom, newSendToSender);
		}
	};

	const stepId = props.step.id;

	return (
		<>
			<Checkbox
				className={css(styleSheet.addTagOnLinkClickCheckbox)}
				id={stepId ? `automation-send-from-checkbox-${stepId}` : uuid()}
				checked={showingSendFrom}
				disabled={(props.isBusy ?? false) || !canEdit}
				type='large'
				onChange={onShowSendFromCheckboxChanged}
			>
				<div>
					<p className={css(styleSheet.checkboxText)}>{props?.sendFromLabel ?? 'Always send this step from'}</p>
				</div>
			</Checkbox>
			{showingSendFrom ? (
				<Select
					value={
						sendFromOptions.find(
							o => o.dataContext.mode === sendFrom?.mode && o.dataContext.connectionType === sendFrom?.connectionType
						)?.id ?? ''
					}
					onValueChange={(value: string) => {
						const option = sendFromOptions.find(o => o.id === value);
						if (option) {
							onSendFromSelectionChanged(option, true);
						}
					}}
				>
					<SelectTrigger>
						<span>{getSendFromOptionLabel(sendFrom) ?? 'Please select...'}</span>
					</SelectTrigger>
					<SelectContent>
						{sendFromOptions.map(option => (
							<SelectItem key={option.id} value={option.id}>
								{option.text}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			) : null}
			{props?.showInternalSendFromOption && showInternalSendOption ? (
				<>
					<Checkbox
						className={css(styleSheet.addTagOnLinkClickCheckbox)}
						id={stepId ? `automation-send-to-checkbox-${stepId}` : uuid()}
						checked={sendToSender}
						disabled={(props.isBusy ?? false) || !canEdit}
						type='large'
						onChange={onSendToSenderCheckboxChanged}
					>
						<div>
							<p className={css(styleSheet.checkboxText)}>Send this email to an internal user</p>
						</div>
					</Checkbox>
					{sendToSender ? (
						<Select
							value={
								sendFromOptions.find(
									o =>
										o.dataContext.mode === sendFrom?.mode && o.dataContext.connectionType === sendFrom?.connectionType
								)?.id ?? ''
							}
							onValueChange={(value: string) => {
								const option = sendFromOptions.find(o => o.id === value);
								if (option) {
									onSendToSenderSelectionChanged(option, true);
								}
							}}
						>
							<SelectTrigger>
								<span>{getSendFromOptionLabel(sendFrom) ?? 'Please select...'}</span>
							</SelectTrigger>
							<SelectContent>
								{sendFromOptions.map(option => (
									<SelectItem key={option.id} value={option.id}>
										{option.text}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					) : null}
				</>
			) : null}
			{selectAnEmployeeModalIsOpen ? (
				<SelectAnEmployeeModal
					bodyText={
						employeeModalForSendToSender ? SelectAnEmployeeInternalEmailBody : SelectAnEmployeeExternalEmailBody
					}
					impersonationContext={props.impersonationContext}
					onClose={onCloseSelectAnEmployeeModal}
					onUserSelected={onSaveSelectAnEmployee}
				/>
			) : null}
		</>
	);
}
